import { ShopperAgentCapabilityKind } from "@redotech/redo-shopper-agent-common/capabilities/shopper-agent-capabilities";

/** Pages under `commerce-agent/playbook`, each a sub item in the nav. */
export enum PlaybookPage {
  BRAND = "brand",
  CAPABILITIES = "capabilities",
  GUARDRAILS = "guardrails",
  CHANNELS = "channels",
}

/** Channel detail pages under `commerce-agent/playbook/channels`. */
export enum PlaybookChannel {
  EMAIL = "email",
  SMS = "sms",
  RCS = "rcs",
  IMESSAGE = "imessage",
  RECOVER = "recover",
}

const PLAYBOOK_ROOT = "playbook";

/** Sub route under the Commerce Agent root, for `getShopperAgentSubRoute`. */
export function playbookPagePath(page: PlaybookPage): string {
  return `${PLAYBOOK_ROOT}/${page}`;
}

export function playbookCapabilityPath(
  kind: ShopperAgentCapabilityKind,
): string {
  return `${playbookPagePath(PlaybookPage.CAPABILITIES)}/${kind}`;
}

export function playbookChannelPath(channel: PlaybookChannel): string {
  return `${playbookPagePath(PlaybookPage.CHANNELS)}/${channel}`;
}

export function toShopperAgentCapabilityKind(
  value: string | undefined,
): ShopperAgentCapabilityKind | undefined {
  return Object.values(ShopperAgentCapabilityKind).find(
    (kind) => kind === value,
  );
}

/** `<SettingWrapper>` ids for playbook controls, matched by `?setting=`. */
export enum PlaybookSetting {
  TONE_AND_VOICE = "playbook/tone-and-voice",
  BRAND_BIO = "playbook/brand-bio",
  PERSONA_NAME = "playbook/persona-name",
  BRAND_NAME = "playbook/brand-name",
  DISCOUNT = "playbook/discount",
  ESCALATION = "playbook/escalation",
  REPLY = "playbook/reply",
  COOLDOWN = "playbook/cooldown",
  SUPERVISOR_GUIDELINES = "playbook/supervisor-guidelines",
  EMAIL_SIGNATURE = "playbook/email-signature",
  EMAIL_CHANNEL = "playbook/email-channel",
  SMS_CHANNEL = "playbook/sms-channel",
  RCS = "playbook/rcs",
  IMESSAGE = "playbook/imessage",
  RECOVER_OUTREACH = "playbook/recover-outreach",
  EXCLUDE_SMS_UNSUBSCRIBERS = "playbook/exclude-sms-unsubscribers",
}

const settingPage: Record<PlaybookSetting, string> = {
  [PlaybookSetting.TONE_AND_VOICE]: playbookPagePath(PlaybookPage.BRAND),
  [PlaybookSetting.BRAND_BIO]: playbookPagePath(PlaybookPage.BRAND),
  [PlaybookSetting.PERSONA_NAME]: playbookPagePath(PlaybookPage.BRAND),
  [PlaybookSetting.BRAND_NAME]: playbookPagePath(PlaybookPage.BRAND),
  [PlaybookSetting.DISCOUNT]: playbookCapabilityPath(
    ShopperAgentCapabilityKind.DISCOUNTS,
  ),
  [PlaybookSetting.ESCALATION]: playbookCapabilityPath(
    ShopperAgentCapabilityKind.ESCALATE_TO_SUPPORT,
  ),
  [PlaybookSetting.REPLY]: playbookCapabilityPath(
    ShopperAgentCapabilityKind.ESCALATE_TO_SUPPORT,
  ),
  [PlaybookSetting.COOLDOWN]: playbookPagePath(PlaybookPage.GUARDRAILS),
  [PlaybookSetting.SUPERVISOR_GUIDELINES]: playbookPagePath(
    PlaybookPage.GUARDRAILS,
  ),
  [PlaybookSetting.EMAIL_SIGNATURE]: playbookChannelPath(PlaybookChannel.EMAIL),
  [PlaybookSetting.EMAIL_CHANNEL]: playbookChannelPath(PlaybookChannel.EMAIL),
  [PlaybookSetting.SMS_CHANNEL]: playbookChannelPath(PlaybookChannel.SMS),
  [PlaybookSetting.RCS]: playbookChannelPath(PlaybookChannel.RCS),
  [PlaybookSetting.IMESSAGE]: playbookChannelPath(PlaybookChannel.IMESSAGE),
  [PlaybookSetting.RECOVER_OUTREACH]: playbookChannelPath(
    PlaybookChannel.RECOVER,
  ),
  [PlaybookSetting.EXCLUDE_SMS_UNSUBSCRIBERS]: playbookChannelPath(
    PlaybookChannel.RECOVER,
  ),
};

/**
 * `?field=` values from the single-page playbook, which built them with
 * `playbookFieldId(label)` from the labels of its Brand pane.
 */
const legacyFieldSettings: Record<string, PlaybookSetting> = {
  "playbook-section-ai-tone-and-voice": PlaybookSetting.TONE_AND_VOICE,
  "playbook-section-brand-bio": PlaybookSetting.BRAND_BIO,
  "playbook-section-persona": PlaybookSetting.PERSONA_NAME,
  "playbook-section-email-signature": PlaybookSetting.EMAIL_SIGNATURE,
  "playbook-section-discount": PlaybookSetting.DISCOUNT,
  "playbook-section-cooldown": PlaybookSetting.COOLDOWN,
  "playbook-section-reply": PlaybookSetting.REPLY,
  "playbook-section-escalate-to-support": PlaybookSetting.ESCALATION,
  "playbook-section-extended-outreach": PlaybookSetting.RECOVER_OUTREACH,
  "playbook-section-imessage": PlaybookSetting.IMESSAGE,
  "playbook-section-rcs": PlaybookSetting.RCS,
  "playbook-section-supervisor-guidelines":
    PlaybookSetting.SUPERVISOR_GUIDELINES,
};

const SETTING_QUERY_PARAM = "setting";
const LEGACY_SECTION_QUERY_PARAM = "section";
const LEGACY_FIELD_QUERY_PARAM = "field";
const LEGACY_CAPABILITIES_SECTION = "brand:capabilities";
const LEGACY_CAPABILITY_SECTION_PREFIX = "capability:";

/** Sub route that owns a setting, with `?setting=` so the page scrolls to it. */
export function playbookSettingPath(setting: PlaybookSetting): string {
  return `${settingPage[setting]}?${SETTING_QUERY_PARAM}=${encodeURIComponent(setting)}`;
}

function toPlaybookSetting(field: string): PlaybookSetting | undefined {
  return (
    Object.values(PlaybookSetting).find((setting) => setting === field) ??
    legacyFieldSettings[field]
  );
}

function legacySectionPath(section: string): string {
  if (section === LEGACY_CAPABILITIES_SECTION) {
    return playbookPagePath(PlaybookPage.CAPABILITIES);
  }
  if (section.startsWith(LEGACY_CAPABILITY_SECTION_PREFIX)) {
    const kind = toShopperAgentCapabilityKind(
      section.slice(LEGACY_CAPABILITY_SECTION_PREFIX.length),
    );
    return kind
      ? playbookCapabilityPath(kind)
      : playbookPagePath(PlaybookPage.CAPABILITIES);
  }
  return playbookPagePath(PlaybookPage.BRAND);
}

/**
 * Where a `?section=` / `?field=` link from the single-page playbook now
 * lands. A known field wins over the section: only its page can scroll to it.
 */
export function resolveLegacyPlaybookLocation(
  search: URLSearchParams,
): { path: string; search: URLSearchParams } | undefined {
  const section = search.get(LEGACY_SECTION_QUERY_PARAM);
  const field = search.get(LEGACY_FIELD_QUERY_PARAM);
  if (section === null && field === null) {
    return undefined;
  }
  const next = new URLSearchParams(search);
  next.delete(LEGACY_SECTION_QUERY_PARAM);
  next.delete(LEGACY_FIELD_QUERY_PARAM);

  const setting = field ? toPlaybookSetting(field) : undefined;
  if (setting) {
    next.set(SETTING_QUERY_PARAM, setting);
    return { path: settingPage[setting], search: next };
  }
  return { path: legacySectionPath(section ?? ""), search: next };
}
